<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import heroBanner from '../assets/hero-body-scanners-base.webp';
import { getBootstrap, submitInquiry, type Contact } from '../api';
import { locale, text } from '../i18n';

const route = useRoute();
const status = ref<'idle' | 'sending' | 'success' | 'error'>('idle');
const message = ref('');
const form = reactive({ name: '', company: '', email: '', phone: '', message: '' });
const contact = ref<Contact | null>(null);
const contactLoading = ref(true);

watch(locale, (currentLocale) => { void loadContact(currentLocale); }, { immediate: true });

async function loadContact(currentLocale: 'zh-CN' | 'en-US') {
  contactLoading.value = true;
  try {
    contact.value = (await getBootstrap(currentLocale)).contact;
  } catch {
    contact.value = null;
  } finally {
    contactLoading.value = false;
  }
}

function channelHref(channel: Contact['channels'][number]): string | null {
  if (channel.href) return channel.href;
  if (channel.type === 'phone') return `tel:${channel.value.replace(/[^+\d]/g, '')}`;
  if (channel.type === 'email') return `mailto:${channel.value}`;
  if ((channel.type === 'social' || channel.type === 'website') && /^https?:\/\//i.test(channel.value)) return channel.value;
  return null;
}

function isExternalHref(href: string | null): boolean { return Boolean(href && /^https?:\/\//i.test(href)); }

async function submit() {
  status.value = 'sending';
  message.value = '';
  try {
    await submitInquiry({
      productId: typeof route.query.product === 'string' ? Number(route.query.product) : null,
      name: form.name,
      company: form.company || null,
      email: form.email,
      phone: form.phone,
      message: form.message,
      locale: locale.value,
      sourcePath: window.location.pathname
    });
    status.value = 'success';
    message.value = text('需求已提交，我们会尽快与您联系。', 'Your inquiry was submitted. Our team will contact you soon.');
    Object.assign(form, { name: '', company: '', email: '', phone: '', message: '' });
  } catch (reason) {
    status.value = 'error';
    message.value = reason instanceof Error ? reason.message : text('提交失败，请稍后重试。', 'Submission failed. Please try again.');
  }
}
</script>

<template>
  <div class="legacy-pages">
    <section class="portal-page-hero portal-page-hero--contact">
      <img
        :src="heroBanner"
        :alt="text('技卫士人体扫描设备与人体成像效果', 'JWS body scanners and personnel imaging results')"
      >
      <div class="container">
        <span class="portal-eyebrow">{{ text('联系我们', 'Contact') }}</span>
        <h1>{{ text('从项目需求开始建立清晰沟通', 'Clear communication from the first project requirement') }}</h1>
        <p>{{ text('请提供应用场景、检查对象和现场条件，营销中心将协同技术团队与您联系。', 'Share your application, screening targets and site conditions. Our sales and technical teams will respond.') }}</p>
      </div>
    </section>

    <section class="portal-section">
      <div class="container portal-contact-grid">
        <div class="portal-contact-info">
          <span class="portal-eyebrow">{{ text('营销中心', 'Sales team') }}</span>
          <h2>{{ text('项目咨询与技术支持', 'Project consultation and technical support') }}</h2>
          <p v-if="contact?.introHtml" v-html="contact.introHtml" />
          <p v-else-if="contactLoading">{{ text('正在加载联系资料…', 'Loading contact details…') }}</p>
          <p v-else>{{ text('请通过以下方式联系营销中心。', 'Please contact our sales team using the details below.') }}</p>
          <dl v-if="contact?.channels.length">
            <div v-for="channel in contact.channels" :key="`${channel.type}-${channel.label}-${channel.value}`"><dt>{{ channel.label }}</dt><dd><a v-if="channelHref(channel)" :href="channelHref(channel) || undefined" :target="isExternalHref(channelHref(channel)) ? '_blank' : undefined" :rel="isExternalHref(channelHref(channel)) ? 'noreferrer' : undefined">{{ channel.value }}</a><span v-else>{{ channel.value }}</span></dd></div>
          </dl>
          <p v-else-if="!contactLoading" class="portal-contact-info__empty">{{ text('暂未配置公开联系渠道。', 'No public contact channels are configured.') }}</p>
          <div v-if="contact?.address" class="portal-contact-address">
            <strong>{{ text('办公地址', 'Office address') }}</strong><p>{{ contact.address }}</p>
          </div>
        </div>

        <form
          class="portal-contact-form"
          @submit.prevent="submit"
        >
          <div class="portal-contact-form__heading">
            <span>{{ text('在线留言', 'Inquiry') }}</span><h2>{{ text('提交项目需求', 'Submit project requirements') }}</h2>
            <p v-if="route.query.name">
              {{ text(`咨询产品：${route.query.name}`, `Product: ${route.query.name}`) }}
            </p>
            <p v-else>
              {{ text('带 * 项为必填，信息仅用于本次业务联系。', 'Fields marked * are required. Information is used only for this business inquiry.') }}
            </p>
          </div>
          <label class="portal-field portal-field--full"><span>{{ text('需求说明 *', 'Requirements *') }}</span><textarea
            v-model="form.message"
            rows="6"
            required
            minlength="10"
            :placeholder="text('请简要说明应用场景、检查对象、预计数量及现场条件', 'Describe the application, screening targets, quantity and site conditions')"
          /></label>
          <label class="portal-field"><span>{{ text('姓名 *', 'Name *') }}</span><input
            v-model="form.name"
            required
            autocomplete="name"
            :placeholder="text('姓名', 'Name')"
          ></label>
          <label class="portal-field"><span>{{ text('公司名称', 'Company') }}</span><input
            v-model="form.company"
            autocomplete="organization"
            :placeholder="text('公司名称', 'Company')"
          ></label>
          <label class="portal-field"><span>{{ text('电话 *', 'Phone *') }}</span><input
            v-model="form.phone"
            required
            autocomplete="tel"
            :placeholder="text('电话', 'Phone')"
          ></label>
          <label class="portal-field"><span>{{ text('邮箱 *', 'Email *') }}</span><input
            v-model="form.email"
            type="email"
            required
            autocomplete="email"
            :placeholder="text('邮箱', 'Email')"
          ></label>
          <div class="portal-contact-form__submit">
            <button
              class="button"
              type="submit"
              :disabled="status === 'sending'"
            >
              {{ status === 'sending' ? text('正在提交…', 'Submitting…') : text('提交需求', 'Submit inquiry') }}
            </button>
            <span
              :class="status"
              role="status"
              aria-live="polite"
            >{{ message }}</span>
          </div>
        </form>
      </div>
    </section>
  </div>
</template>
